"use client";

import { useEffect, useRef } from "react";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
  onTimeout?: () => void;
  className?: string;
}

// Track global script loading state
let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks: (() => void)[] = [];

function loadScript(): Promise<void> {
  if (scriptLoaded) return Promise.resolve();

  return new Promise((resolve) => {
    if (scriptLoading) {
      loadCallbacks.push(resolve);
      return;
    }
    scriptLoading = true;

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      resolve(); // Degrade gracefully — don't block the form
    };
    document.head.appendChild(script);
  });
}

export default function Turnstile({
  onVerify,
  onExpire,
  onError,
  theme = "auto",
  size = "flexible",
  className,
  resetKey,
}: TurnstileProps & { resetKey?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Keep callbacks fresh without triggering useEffect re-run
  useEffect(() => {
    callbacksRef.current = { onVerify, onExpire, onError };
  });

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let mounted = true;

    loadScript().then(() => {
      if (!mounted || !containerRef.current) return;
      if (typeof window.turnstile === "undefined") return;

      // Clean up any existing widget
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already be removed
        }
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        callback: (token: string) => callbacksRef.current.onVerify(token),
        "expired-callback": () => callbacksRef.current.onExpire?.(),
        "error-callback": () => callbacksRef.current.onError?.(),
        "refresh-expired": "auto",
        retry: "auto",
        "retry-interval": 2000,
        "timeout-callback": () => callbacksRef.current.onError?.(),
      });
    });

    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && typeof window.turnstile !== "undefined") {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, size, resetKey]);

  if (!siteKey) return null;

  return <div ref={containerRef} className={className} />;
}

// Reset function for external use (e.g., after form submission failure)
export function resetTurnstile(widgetId: string | null) {
  if (widgetId !== null && typeof window !== "undefined" && window.turnstile) {
    try {
      window.turnstile.reset(widgetId);
    } catch {
      // Ignore
    }
  }
}

// TypeScript declaration for Turnstile global
declare global {
  interface Window {
    turnstile: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}
